import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import * as workflowService from "../../services/workflowService";

// The steps of booking workflows that are waiting on one person (drop-off, cleaning,
// pick-up ...). Crew see their own and can start / finish them; staff pick a person and
// can do the same on their behalf. A step that follows another stays locked until that
// one is done, and says so.

const cardStyle = { border: "1px solid #E3EBF8", borderRadius: "10px", padding: "14px 16px", background: "#fff" };

const dayLabel = (iso) => {
  if (!iso) return "No date";
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
};

// The message to show for an answer that did not succeed (a lost race, a locked step ...).
export const failureMessage = (res) => res?.message || "Something went wrong";

function TaskCard({ task, busy, onAct }) {
  const started = task.state === "IN_PROGRESS";
  return (
    <div style={cardStyle} data-testid={`task-${task.group_id}-${task.step_key}`}>
      <div className="d-flex flex-wrap align-items-start justify-content-between" style={{ gap: "10px" }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: "15px" }}>
            {task.name}
            {!task.is_mandatory && <span style={{ fontSize: "11px", color: "#6B7280", marginLeft: "8px" }}>Optional</span>}
          </div>
          <div style={{ fontSize: "13px", color: "#374151" }}>
            {task.services || task.workflow_name}
            {task.service_time ? ` · ${String(task.service_time).slice(0, 5)}` : ""}
          </div>
          <div style={{ fontSize: "12px", color: "#6B7280" }}>
            <Link to={`/vendor_bookings/${encodeURIComponent(task.display_booking_id || task.group_id)}`}>
              {task.display_booking_id || `Booking ${task.group_id}`}
            </Link>
            {started && <span style={{ marginLeft: "8px", color: "#2E7D32" }}>In progress</span>}
          </div>
          {task.locked && (
            <div style={{ fontSize: "12px", color: "#B45309", marginTop: "4px" }}>Waiting for "{task.blocked_by}" to be done first</div>
          )}
        </div>
        <div className="d-flex" style={{ gap: "8px" }}>
          {!started && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={busy || task.locked}
              onClick={() => onAct(task, "START")}
            >
              Start
            </button>
          )}
          <button type="button" className="btn btn-sm text-white" style={{ background: "#C2185B" }} disabled={busy || task.locked} onClick={() => onAct(task, "DONE")}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function MyTasks() {
  const isCrew = apicall.getRole() === "5";
  const [people, setPeople] = useState([]);
  const [personId, setPersonId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyKey, setBusyKey] = useState("");

  const load = useCallback(async () => {
    if (!isCrew && !personId) {
      setTasks([]);
      setLoaded(true);
      return;
    }
    try {
      const res = await workflowService.getMyTasks(isCrew ? undefined : personId);
      setTasks(res?.success ? res.data : []);
    } catch (e) {
      setTasks([]);
    }
    setLoaded(true);
  }, [isCrew, personId]);

  useEffect(() => {
    if (isCrew) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await workflowService.getEligibleCrew({ skill: "ANY" });
        if (!cancelled && res?.success) setPeople(res.data.crew || []);
      } catch (e) {
        if (!cancelled) setPeople([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCrew]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (task, action) => {
    const key = `${task.group_id}-${task.step_key}`;
    setBusyKey(key);
    try {
      const res = await workflowService.changeStepState({ groupId: task.group_id, stepKey: task.step_key, action });
      if (res?.success) {
        if (res.data?.status_changed) toast.success(`Booking moved to ${res.data.status_changed}`);
        else toast.success(action === "START" ? "Started" : "Done");
      } else {
        toast.error(failureMessage(res));
      }
    } catch (e) {
      toast.error("Something went wrong");
    }
    setBusyKey("");
    load();
  };

  const byDay = tasks.reduce((acc, t) => {
    (acc[t.service_date || ""] = acc[t.service_date || ""] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="p-3 p-md-4" style={{ maxWidth: "820px" }}>
      <h5 className="page-heading mb-3">{isCrew ? "My Tasks" : "Tasks"}</h5>

      {!isCrew && (
        <div className="mb-3" style={{ maxWidth: "320px" }}>
          <label className="form-label" htmlFor="task-person">
            Person
          </label>
          <select id="task-person" className="form-select" value={personId} onChange={(e) => setPersonId(e.target.value)}>
            <option value="">Choose a person</option>
            {people.map((p) => (
              <option key={p.user_id} value={p.user_id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loaded && tasks.length === 0 && (
        <div style={{ color: "#6B7280" }}>{isCrew || personId ? "Nothing waiting for you right now." : "Choose a person to see their tasks."}</div>
      )}

      {Object.keys(byDay)
        .sort()
        .map((day) => (
          <div key={day} className="mb-4">
            <div style={{ fontWeight: 600, marginBottom: "8px" }}>{dayLabel(day)}</div>
            <div className="d-flex flex-column" style={{ gap: "10px" }}>
              {byDay[day].map((t) => (
                <TaskCard key={`${t.group_id}-${t.step_key}`} task={t} busy={busyKey === `${t.group_id}-${t.step_key}`} onAct={act} />
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

export default MyTasks;
